export const EXCHANGE_REASONS = [
  ['WRONG_ITEM', 'Wrong Item'],
  ['DAMAGED', 'Damaged or Defective'],
  ['NOT_AS_DESCRIBED', 'Not as Described'],
  ['MISSING_ITEM_OR_PARTS', 'Missing Item or Parts'],
  ['DOES_NOT_FIT', "Doesn't Fit"],
  ['CHANGED_MIND', 'Changed Mind'],
  ['ARRIVED_LATE', 'Arrived Late'],
  ['OTHER', 'Other'],
] as const;
export type ExchangeRequirement = 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';
export type ExchangeDefaultRequirement = ExchangeRequirement | 'REASON_BASED';
export interface LegacyDifferentProductPolicy {
  enabled: boolean;
  eligibleOriginals: { productIds: string[]; collectionIds: string[] };
  reasonIds: string[];
  replacements: {
    mode: 'CATALOGUE' | 'PRODUCT_ALLOWLIST' | 'COLLECTION_ALLOWLIST';
    productIds: string[];
    collectionIds: string[];
  };
}
export interface DifferentProductPolicy {
  enabled: boolean;
  includeOtherOrderedProducts?: boolean;
  originalScope: {
    mode: 'ALL_ELIGIBLE' | 'FILTERED';
    includeProductIds: string[];
    includeCollectionIds: string[];
    excludeProductIds: string[];
    excludeCollectionIds: string[];
  };
  reasonScope: { mode: 'ALL_ENABLED' | 'SELECTED'; reasonIds: string[] };
  replacements: LegacyDifferentProductPolicy['replacements'] | {
    mode: 'FILTERED';
    productIds: string[];
    collectionIds: string[];
    productTags: string[];
  };
}
export const normalizeReplacementProductTags = (values: string[]): string[] =>
  Array.from(new Set(values.map(value => value.trim().toLowerCase()))).sort();
export function differentProductForEditing(policy: ExchangePolicy | null): DifferentProductPolicy {
  const current = policy?.differentProduct;
  if (current && 'originalScope' in current) return current;
  const configured = current && (current.enabled || current.eligibleOriginals.productIds.length > 0 || current.eligibleOriginals.collectionIds.length > 0 || current.reasonIds.length > 0);
  return {
    enabled: current?.enabled ?? false,
    originalScope: {
      mode: configured ? 'FILTERED' : 'ALL_ELIGIBLE',
      includeProductIds: [...(current?.eligibleOriginals.productIds ?? [])],
      includeCollectionIds: [...(current?.eligibleOriginals.collectionIds ?? [])],
      excludeProductIds: [], excludeCollectionIds: [],
    },
    reasonScope: configured ? {mode: 'SELECTED', reasonIds: [...current!.reasonIds]} : {mode: 'ALL_ENABLED', reasonIds: []},
    replacements: current?.replacements ?? {mode: 'CATALOGUE', productIds: [], collectionIds: []},
  };
}
export interface ExchangePolicy {
  version: 1 | 2;
  enabled: boolean;
  eligibleOriginals: {
    mode?: 'ALL_POLICY_ELIGIBLE' | 'SELECTED';
    productIds: string[];
    collectionIds: string[];
  };
  reasons: {
    enabledPresetReasons: string[];
    enabledCustomReasonIds: string[];
    customReasons: Array<{ id: string; label: string; analyticsKey: string }>;
  };
  evidenceRequirements: {
    defaults: {
      notes: ExchangeDefaultRequirement;
      photos: ExchangeDefaultRequirement;
    };
    byReason: Record<
      string,
      Partial<{ notes: ExchangeRequirement; photos: ExchangeRequirement }>
    >;
  };
  manualReviewReasonIds: string[];
  differentProduct?: DifferentProductPolicy | LegacyDifferentProductPolicy;
}
export interface ExchangeSettingsRecord {
  storeId: string;
  revision: number;
  configured: boolean;
  policy: ExchangePolicy;
}
const object = (value: any) =>
  value && typeof value === 'object' && !Array.isArray(value);
const ids = (value: any, pattern: RegExp) =>
  Array.isArray(value) &&
  value.length <= 100 &&
  value.every((id) => typeof id === 'string' && pattern.test(id)) &&
  new Set(value).size === value.length;
const keys = (value: any, required: string[]) =>
  object(value) &&
  Object.keys(value).length === required.length &&
  required.every((key) => Object.prototype.hasOwnProperty.call(value, key));
const modes = ['HIDDEN', 'OPTIONAL', 'REQUIRED'];
function canonicalDifferentProduct(policy: DifferentProductPolicy | LegacyDifferentProductPolicy): DifferentProductPolicy | LegacyDifferentProductPolicy {
  if ('originalScope' in policy) return {...policy,
    originalScope: {...policy.originalScope,
      includeProductIds: [...policy.originalScope.includeProductIds].sort(),
      includeCollectionIds: [...policy.originalScope.includeCollectionIds].sort(),
      excludeProductIds: [...policy.originalScope.excludeProductIds].sort(),
      excludeCollectionIds: [...policy.originalScope.excludeCollectionIds].sort()},
    reasonScope: {...policy.reasonScope, reasonIds: [...policy.reasonScope.reasonIds].sort()},
    replacements: {...policy.replacements,
      productIds: [...policy.replacements.productIds].sort(), collectionIds: [...policy.replacements.collectionIds].sort(),
      ...(policy.replacements.mode === 'FILTERED' ? {productTags: normalizeReplacementProductTags(policy.replacements.productTags)} : {})}};
  return {...policy,
    eligibleOriginals: {productIds: [...policy.eligibleOriginals.productIds].sort(), collectionIds: [...policy.eligibleOriginals.collectionIds].sort()},
    reasonIds: [...policy.reasonIds].sort(),
    replacements: {...policy.replacements, productIds: [...policy.replacements.productIds].sort(), collectionIds: [...policy.replacements.collectionIds].sort()}};
}
export function canonicalExchangePolicy(
  policy: ExchangePolicy,
): ExchangePolicy {
  return {
    ...policy,
    eligibleOriginals: {
      ...(Object.prototype.hasOwnProperty.call(policy.eligibleOriginals, 'mode') ? {mode: policy.eligibleOriginals.mode} : {}),
      productIds: [...policy.eligibleOriginals.productIds].sort(),
      collectionIds: [...policy.eligibleOriginals.collectionIds].sort(),
    },
    reasons: {
      enabledPresetReasons: [...policy.reasons.enabledPresetReasons].sort(),
      enabledCustomReasonIds: [...policy.reasons.enabledCustomReasonIds].sort(),
      customReasons: policy.reasons.customReasons.map(reason => ({...reason, label: reason.label.trim()})).sort((a, b) =>
        a.id.localeCompare(b.id),
      ),
    },
    manualReviewReasonIds: [...policy.manualReviewReasonIds].sort(),
    ...(policy.differentProduct ? {differentProduct: canonicalDifferentProduct(policy.differentProduct)} : {}),
  };
}
export function exchangePolicyKey(value: unknown): string {
  return JSON.stringify(value, (_, entry) =>
    object(entry)
      ? Object.fromEntries(
          Object.keys(entry)
            .sort()
            .map((key) => [key, entry[key]]),
        )
      : entry,
  );
}
export function parseExchangeSettings(
  value: any,
  storeId: string,
): ExchangeSettingsRecord | null {
  if (
    !object(value) ||
    value.storeId !== storeId ||
    !Number.isInteger(value.revision) ||
    value.revision < 0 ||
    value.revision > 2147483647 ||
    typeof value.configured !== 'boolean'
  )
    return null;
  const p = value.policy;
  const policyKeys = [
    'version', 'enabled', 'eligibleOriginals', 'reasons',
    'evidenceRequirements', 'manualReviewReasonIds',
  ];
  if (
    !(keys(p, policyKeys) || keys(p, [...policyKeys, 'differentProduct'])) ||
    ![1, 2].includes(p.version) ||
    typeof p.enabled !== 'boolean'
  )
    return null;
  if (
    !(keys(p.eligibleOriginals, ['productIds', 'collectionIds']) ||
      (p.version === 2 && keys(p.eligibleOriginals, ['mode', 'productIds', 'collectionIds']) &&
        ['ALL_POLICY_ELIGIBLE', 'SELECTED'].includes(p.eligibleOriginals.mode))) ||
    !ids(
      p.eligibleOriginals.productIds,
      /^gid:\/\/shopify\/Product\/[1-9]\d*$/,
    ) ||
    !ids(
      p.eligibleOriginals.collectionIds,
      /^gid:\/\/shopify\/Collection\/[1-9]\d*$/,
    )
  )
    return null;
  const allPolicyEligible = p.eligibleOriginals.mode === 'ALL_POLICY_ELIGIBLE';
  if (allPolicyEligible && (p.eligibleOriginals.productIds.length || p.eligibleOriginals.collectionIds.length)) return null;
  const r = p.reasons;
  if (
    !keys(r, [
      'enabledPresetReasons',
      'enabledCustomReasonIds',
      'customReasons',
    ]) ||
    !Array.isArray(r.customReasons) ||
    r.customReasons.length > 100
  )
    return null;
  if (
    r.customReasons.some(
      (reason: any) =>
        !keys(reason, ['id', 'label', 'analyticsKey']) ||
        typeof reason.id !== 'string' ||
        !/^CUSTOM_[A-Z][A-Z0-9_]{0,79}$/.test(reason.id) ||
        typeof reason.label !== 'string' ||
        !reason.label.trim() ||
        reason.label.length > 120 ||
        /[\p{Cc}\p{Cf}]/u.test(reason.label) ||
        typeof reason.analyticsKey !== 'string' ||
        !/^custom_[a-z][a-z0-9_]{0,79}$/.test(reason.analyticsKey),
    )
  )
    return null;
  const customIds = r.customReasons.map((reason: any) => reason.id),
    allIds = [...EXCHANGE_REASONS.map((reason) => reason[0]), ...customIds];
  if (
    new Set(customIds).size !== customIds.length ||
    !ids(r.enabledPresetReasons, /^[A-Z_]+$/) ||
    r.enabledPresetReasons.some(
      (id: string) => !EXCHANGE_REASONS.some((reason) => reason[0] === id),
    ) ||
    !ids(r.enabledCustomReasonIds, /^CUSTOM_[A-Z][A-Z0-9_]{0,79}$/) ||
    r.enabledCustomReasonIds.some((id: string) => !customIds.includes(id))
  )
    return null;
  const e = p.evidenceRequirements;
  if (
    !keys(e, ['defaults', 'byReason']) ||
    !keys(e.defaults, ['notes', 'photos']) ||
    ![e.defaults.notes, e.defaults.photos].every((mode) =>
      [...modes, 'REASON_BASED'].includes(mode),
    ) ||
    !object(e.byReason)
  )
    return null;
  if (
    Object.entries(e.byReason).some(
      ([id, rule]: [string, any]) =>
        !allIds.includes(id) ||
        !object(rule) ||
        Object.entries(rule).some(
          ([key, mode]) =>
            !['notes', 'photos'].includes(key) ||
            !modes.includes(mode as string),
        ),
    )
  )
    return null;
  if (
    !ids(p.manualReviewReasonIds, /^[A-Z0-9_]+$/) ||
    p.manualReviewReasonIds.some((id: string) => !allIds.includes(id))
  )
    return null;
  if (Object.prototype.hasOwnProperty.call(p, 'differentProduct')) {
    const d = p.differentProduct;
    const enabledIds = [...r.enabledPresetReasons, ...r.enabledCustomReasonIds];
    if (p.version === 1) {
      if (!keys(d, ['enabled', 'eligibleOriginals', 'reasonIds', 'replacements']) ||
        !keys(d.eligibleOriginals, ['productIds', 'collectionIds']) ||
        !ids(d.eligibleOriginals.productIds, /^gid:\/\/shopify\/Product\/[1-9]\d*$/) ||
        !ids(d.eligibleOriginals.collectionIds, /^gid:\/\/shopify\/Collection\/[1-9]\d*$/) ||
        !ids(d.reasonIds, /^[A-Z0-9_]+$/) || d.reasonIds.some((id: string) => !enabledIds.includes(id)) ||
        (d.enabled && ((!d.eligibleOriginals.productIds.length && !d.eligibleOriginals.collectionIds.length) || !d.reasonIds.length))) return null;
    } else {
      const s = d?.originalScope, reason = d?.reasonScope;
      const differentKeys = ['enabled', 'originalScope', 'reasonScope', 'replacements'];
      if (!(keys(d, differentKeys) || (keys(d, [...differentKeys, 'includeOtherOrderedProducts']) && typeof d.includeOtherOrderedProducts === 'boolean')) ||
        !keys(s, ['mode', 'includeProductIds', 'includeCollectionIds', 'excludeProductIds', 'excludeCollectionIds']) ||
        !['ALL_ELIGIBLE', 'FILTERED'].includes(s.mode) ||
        !['includeProductIds', 'excludeProductIds'].every(key => ids(s[key], /^gid:\/\/shopify\/Product\/[1-9]\d*$/)) ||
        !['includeCollectionIds', 'excludeCollectionIds'].every(key => ids(s[key], /^gid:\/\/shopify\/Collection\/[1-9]\d*$/)) ||
        (s.mode === 'ALL_ELIGIBLE' && [s.includeProductIds, s.includeCollectionIds, s.excludeProductIds, s.excludeCollectionIds].some(list => list.length)) ||
        !keys(reason, ['mode', 'reasonIds']) || !['ALL_ENABLED', 'SELECTED'].includes(reason.mode) ||
        !ids(reason.reasonIds, /^[A-Z0-9_]+$/) || reason.reasonIds.some((id: string) => !enabledIds.includes(id)) ||
        (reason.mode === 'ALL_ENABLED' && reason.reasonIds.length) ||
        (d.enabled && reason.mode === 'SELECTED' && !reason.reasonIds.length)) return null;
    }
    const filtered = p.version === 2 && d.replacements?.mode === 'FILTERED';
    if (typeof d.enabled !== 'boolean' || !keys(d.replacements, ['mode', 'productIds', 'collectionIds', ...(filtered ? ['productTags'] : [])]) ||
      !ids(d.replacements.productIds, /^gid:\/\/shopify\/Product\/[1-9]\d*$/) ||
      !ids(d.replacements.collectionIds, /^gid:\/\/shopify\/Collection\/[1-9]\d*$/)) return null;
    const destinations = d.replacements;
    if (!(
      (destinations.mode === 'CATALOGUE' && !destinations.productIds.length && !destinations.collectionIds.length) ||
      (destinations.mode === 'PRODUCT_ALLOWLIST' && destinations.productIds.length && !destinations.collectionIds.length) ||
      (destinations.mode === 'COLLECTION_ALLOWLIST' && destinations.collectionIds.length && !destinations.productIds.length) ||
      (filtered && Array.isArray(destinations.productTags) && destinations.productTags.length <= 100 &&
        destinations.productTags.every((tag: unknown) => typeof tag === 'string' && tag === tag.trim() && tag.length >= 1 && tag.length <= 255 && !/[\p{Cc}\p{Cf}]/u.test(tag)) &&
        normalizeReplacementProductTags(destinations.productTags).length === destinations.productTags.length &&
        (destinations.productIds.length || destinations.collectionIds.length || destinations.productTags.length))
    )) return null;
  }
  if (
    p.enabled &&
    ((!allPolicyEligible && !p.eligibleOriginals.productIds.length &&
      !p.eligibleOriginals.collectionIds.length) ||
      (!r.enabledPresetReasons.length && !r.enabledCustomReasonIds.length))
  )
    return null;
  return { ...value, policy: canonicalExchangePolicy(p) };
}
export function exchangeSettingsValidation(
  policy: ExchangePolicy,
): string | null {
  if (
    policy.enabled &&
    policy.eligibleOriginals.mode !== 'ALL_POLICY_ELIGIBLE' &&
    !policy.eligibleOriginals.productIds.length &&
    !policy.eligibleOriginals.collectionIds.length
  )
    return 'Choose at least one original product or collection.';
  if (
    policy.enabled &&
    !policy.reasons.enabledPresetReasons.length &&
    !policy.reasons.enabledCustomReasonIds.length
  )
    return 'Choose at least one Exchange reason.';
  const different = policy.differentProduct;
  if (different?.enabled && 'eligibleOriginals' in different && !different.eligibleOriginals.productIds.length && !different.eligibleOriginals.collectionIds.length)
    return 'Choose original products or collections for different-product exchanges.';
  if (different?.enabled && ('reasonIds' in different ? !different.reasonIds.length : different.reasonScope.mode === 'SELECTED' && !different.reasonScope.reasonIds.length))
    return 'Choose at least one enabled Exchange reason for different-product exchanges.';
  if (different?.replacements.mode === 'PRODUCT_ALLOWLIST' && !different.replacements.productIds.length)
    return 'Choose at least one allowed replacement product.';
  if (different?.replacements.mode === 'COLLECTION_ALLOWLIST' && !different.replacements.collectionIds.length)
    return 'Choose at least one allowed replacement collection.';
  if (different?.replacements.mode === 'FILTERED' && !different.replacements.productIds.length && !different.replacements.collectionIds.length && !different.replacements.productTags.length)
    return 'Choose at least one replacement product, collection, or product tag.';
  return parseExchangeSettings(
    { storeId: 'form', revision: 0, configured: true, policy },
    'form',
  )
    ? null
    : 'Check the Exchange reasons and evidence requirements.';
}
